import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, Animated } from 'react-native';
import { TabView, TabBar } from 'react-native-tab-view';
import type { DbTabProps, TabItem } from './DbTab.types';

// 尝试导入 VirtualizedList context（不同 RN 版本路径可能不同）
let VirtualizedListContextReset: React.FC<{ children: React.ReactNode }> | null = null;
try {
    // React Native 0.73+ 导出路径
    const VLContext = require('react-native/Libraries/Lists/VirtualizedListContext');
    const ContextProvider = VLContext.VirtualizedListContext?.Provider || VLContext.default?.Provider;
    if (ContextProvider) {
        VirtualizedListContextReset = ({ children }: { children: React.ReactNode }) => (
            <ContextProvider value={null}>{children}</ContextProvider>
        );
    }
} catch {
    // 回退：不做处理
}

/**
 * DbTab - 基于 react-native-tab-view 封装的 Tab 组件
 * 
 * 特性：
 * 1. 稳定可靠的 Tab 切换和下划线动画
 * 2. 支持横向滚动 Tab 栏
 * 3. 支持懒加载和缓存
 * 4. 左右滑动联动
 */
const DbTab: React.FC<DbTabProps> = ({
  tabs,
  renderContent,
  activeColor = '#FF2442',
  inactiveColor = '#666666',
  tabBarBgColor = '#FFFFFF',
  indicatorHeight = 3,
  indicatorRadius = 1.5,
  indicatorWidth,
  showIndicator = true,
  fontSize = 14,
  activeFontSize = 15,
  activeFontWeight = '600',
  inactiveFontWeight = '400',
  tabBarHeight = 44,
  scrollable = false,
  lazy = true,
  initialPage = 0,
  onTabChange,
  renderTab,
  style,
  tabBarStyle,
  contentStyle,
  tabItemStyle,
  indicatorStyle,
  // 滑动相关配置
  swipeEnabled = true,
}) => {
  // ====== 状态管理 ======
  const [index, setIndex] = useState(initialPage);
  
  // 构建路由配置
  const [routes] = useState(() =>
    tabs.map((tab, i) => ({
      key: String(i),
      title: tab.title,
      tabData: tab,
    }))
  );

  // ====== 渲染场景 ======
  // 用 VirtualizedListContext.Provider value={null} 重置嵌套上下文
  // 避免 TabBar 内部的 FlatList context 导致子组件中的 FlatList 报嵌套警告
  const renderScene = useCallback(({ route }: { route: any }) => {
    const routeIndex = parseInt(route.key, 10);
    const tab = route.tabData || tabs[routeIndex];
    const content = (
      <View style={[styles.scene, contentStyle]}>
        {renderContent(routeIndex, tab)}
      </View>
    );
    // 如果成功导入了 VirtualizedListContext，用 Provider 重置
    if (VirtualizedListContextReset) {
      return <VirtualizedListContextReset>{content}</VirtualizedListContextReset>;
    }
    return content;
  }, [contentStyle, renderContent, tabs]);

  // ====== 自定义指示器 ======
  const renderIndicator = useCallback((props: any) => {
    const { position, navigationState, getTabWidth } = props;
    const { routes, index: activeIndex } = navigationState;
    
    // 计算总宽度（所有 Tab 的宽度之和）
    const tabWidths = routes.map((_: any, i: number) => getTabWidth(i));
    const totalWidth = tabWidths.reduce((sum: number, w: number) => sum + w, 0);
    
    // 计算当前 Tab 的宽度和位置
    let currentTabWidth = tabWidths[activeIndex] || 0;
    let currentTabLeft = 0;
    for (let i = 0; i < activeIndex; i++) {
      currentTabLeft += tabWidths[i];
    }
    
    // 使用固定宽度或 Tab 宽度
    const finalWidth = indicatorWidth || currentTabWidth;
    
    // 计算偏移量（居中）
    const offset = (currentTabWidth - finalWidth) / 2;
    
    // 创建动画值：基础位置 + 偏移量
    const inputRange = routes.map((_: any, i: number) => i);
    const outputRange = routes.map((_: any, i: number) => {
      let left = 0;
      for (let j = 0; j < i; j++) {
        left += tabWidths[j];
      }
      return left + (tabWidths[i] - finalWidth) / 2;
    });
    
    const translateX = position.interpolate({
      inputRange,
      outputRange,
      extrapolate: 'clamp',
    });
    
    return (
      <Animated.View
        style={[
          {
            position: 'absolute',
            bottom: 0,
            height: indicatorHeight,
            width: finalWidth,
            borderRadius: indicatorRadius,
            backgroundColor: activeColor,
            transform: [{ translateX }],
          },
          indicatorStyle,
          !showIndicator && styles.hiddenIndicator,
        ]}
      />
    );
  }, [indicatorWidth, indicatorHeight, indicatorRadius, activeColor, indicatorStyle, showIndicator]);

  // ====== 自定义 TabBar ======
  const renderTabBar = useCallback((props: any) => {
    return (
      <TabBar
        {...props}
        style={[
          styles.tabBar, 
          { backgroundColor: tabBarBgColor, height: tabBarHeight },
          tabBarStyle
        ]}
        tabStyle={[
          styles.tabItem,
          scrollable ? { width: 'auto' } : { flex: 1 },
          tabItemStyle,
        ]}
        scrollEnabled={scrollable}
        // 使用自定义指示器
        renderIndicator={renderIndicator}
        // 禁用默认指示器样式
        indicatorStyle={styles.hiddenIndicator}
        activeColor={activeColor}
        inactiveColor={inactiveColor}
        pressColor="transparent"
        pressOpacity={0.8}
        renderLabel={({ route, focused }: { route: any; focused: boolean }) => {
          const routeIndex = parseInt(route.key, 10);
          const tab = route.tabData || tabs[routeIndex];
          
          // 自定义渲染
          if (renderTab) {
            return renderTab(tab, focused, routeIndex);
          }
          
          // 默认渲染
          return (
            <Text
              style={{
                fontSize: focused ? activeFontSize : fontSize,
                color: focused ? activeColor : inactiveColor,
                fontWeight: focused ? activeFontWeight : inactiveFontWeight,
                ...tab.titleStyle,
              }}
              numberOfLines={1}
            >
              {route.title}
            </Text>
          );
        }}
      />
    );
  }, [
    tabBarBgColor,
    tabBarHeight,
    tabBarStyle,
    tabItemStyle,
    scrollable,
    activeColor,
    inactiveColor,
    renderTab,
    tabs,
    activeFontSize,
    fontSize,
    activeFontWeight,
    inactiveFontWeight,
    renderIndicator,
  ]);

  // ====== 处理 Tab 切换 ======
  const handleIndexChange = useCallback((newIndex: number) => {
    setIndex(newIndex);
    onTabChange?.(newIndex, tabs[newIndex]);
  }, [onTabChange, tabs]);

  return (
    <TabView
      navigationState={{ index, routes }}
      renderScene={renderScene}
      renderTabBar={renderTabBar}
      onIndexChange={handleIndexChange}
      initialLayout={{ width: 375 }}
      lazy={lazy}
      style={[styles.container, style]}
      swipeEnabled={swipeEnabled}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  tabBar: {
    elevation: 0,
    shadowOpacity: 0,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#E5E5E5',
  },
  tabItem: {
    padding: 0,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  hiddenIndicator: {
    height: 0,
    backgroundColor: 'transparent',
  },
  scene: {
    flex: 1,
  },
});

export default DbTab;
export type { DbTabProps, TabItem };
